import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { getLookupByType } from "../../../api/t2tVendorLookupApi";
import { getData } from "../../../FetchApiServices";

// BUG-46 follow-up — Indian states + UTs (28 + 8) and a curated B2B country list.
// Mirrors the constants in T2TVendorSignup.js (public sign-up form, BUG-13).
// Could be extracted into a shared module later; inlined here to keep BUG-46
// follow-up scope tight.
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland",
  "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi",
  "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

// BUG-49: country options now load from /api/t2t/lookups/country via useEffect.
// Hardcoded list deleted; FALLBACK_COUNTRY_OPTIONS kept only for the brief
// window before the API fetch resolves so the dropdown isn't empty.
const FALLBACK_COUNTRY_OPTIONS = [
  { value: "IN", label: "India" },
  { value: "US", label: "United States" },
  { value: "GB", label: "United Kingdom" },
];

// Helper: India Post returns "Delhi", "Uttar Pradesh", etc. Map to canonical form.
const matchIndianState = (apiName) => {
  if (!apiName) return null;
  const norm = apiName.toLowerCase().trim();
  return INDIAN_STATES.find((s) => s.toLowerCase() === norm) || null;
};

const TABS = [
  { id: "initial",   label: "1. Initial" },
  { id: "general",   label: "2. General Data" },
  { id: "tax",       label: "3. Tax" },
  { id: "finance",   label: "4. Finance" },
  { id: "purchasing",label: "5. Purchasing" },
  { id: "partner",   label: "6. Partner Function" },
];

const ROW = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" };
const FG  = { marginBottom: 14 };
const LBL = { display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 };
const FC  = { width: "100%", padding: "7px 10px", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" };
const FC_ERR = { ...FC, borderColor: "#DC2626" };
const ERR_TXT = { fontSize: 11, color: "#DC2626", marginTop: 3 };
const REQ_STAR = { color: "#DC2626", marginLeft: 2 };

// ─── Helpers ─────────────────────────────────────────────────────────────────
const panRegex    = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const gstinRegex  = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const mobileRegex = /^[0-9]{10,15}$/;
const ifscRegex   = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const junkChars   = /[*#@!$%^&()_+\\|~=[\]{}:"';<>?./]/;

// BUG-58: SAP / govt-ID fields are always uppercase in source systems.
// We normalise on input so what's stored matches what the backend regex expects,
// and validate() can test the raw value (no .toUpperCase() inside test).
const UPPERCASE_FIELDS = new Set(["pan", "gstin", "ifsc_code", "swift_code", "iban", "cin"]);

const GSTIN_PAN_MISMATCH_MSG = "GSTIN doesn't match PAN — chars 3-12 of GSTIN should equal the PAN";

// BUG-50: phone validation moved into the component so it can read live country
// metadata. The static `validate()` below no longer enforces contact_phone — it
// returns null and the component-level validator takes over.
function validate(key, value) {
  if (!value) return null;
  // Junk chars check FIRST so the specific message shows before pattern errors
  if (["pan", "bank_account_number", "contact_phone"].includes(key)) {
    if (junkChars.test(value)) return "Contains invalid characters (* # @ ! $ % ^ & etc.)";
  }
  if (key === "pan")            { if (!panRegex.test(value))                 return "Invalid PAN — must be 10 chars uppercase (e.g. ABCDE1234F)"; }
  if (key === "gstin")          { if (!gstinRegex.test(value))               return "Invalid GSTIN — must be 15 chars uppercase (e.g. 22AAAAA0000A1Z5)"; }
  // contact_phone validation moved into the component (country-aware) — see Phone field render.
  if (key === "pin_code")       { if (!/^\d{6}$/.test(value))                return "Must be exactly 6 digits"; }
  if (key === "ifsc_code")      { if (!ifscRegex.test(value))                return "Invalid IFSC (e.g. HDFC0001234)"; }
  return null;
}

// Fields with exact length — shows live counter
const EXACT_LEN  = { pan: 10, gstin: 15, pin_code: 6, ifsc_code: 11 };
const RANGE_LEN  = { contact_phone: { min: 10, max: 15 } };

// ─── Field row helpers ────────────────────────────────────────────────────────
function Field({ label, name, value, onChange, onBlur, readOnly, type = "text", required, options, placeholder, hint, errors }) {
  const err = errors?.[name];
  const inputStyle = err ? FC_ERR : { ...FC, background: readOnly ? "#F8FAFC" : "#fff" };
  const exactLen  = EXACT_LEN[name];
  const rangeLen  = RANGE_LEN[name];
  const currentLen = (value || "").length;

  return (
    <div style={FG}>
      <label style={LBL}>
        {label}{required && <span style={REQ_STAR}>*</span>}
      </label>
      {options ? (
        <select style={inputStyle} value={value || ""} disabled={readOnly}
          onChange={(e) => onChange(name, e.target.value)}
          onBlur={onBlur ? () => onBlur(name, value) : undefined}>
          <option value="">Select {label}…</option>
          {options.map((o) => (
            <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
          ))}
        </select>
      ) : type === "textarea" ? (
        <textarea rows={3} style={inputStyle} value={value || ""} readOnly={readOnly}
          placeholder={readOnly ? "" : (placeholder || `Enter ${label}…`)}
          onChange={(e) => onChange(name, e.target.value)}
          onBlur={onBlur ? () => onBlur(name, value) : undefined} />
      ) : (
        <input style={inputStyle} type={type} value={value || ""} readOnly={readOnly}
          placeholder={readOnly ? "" : (placeholder || `Enter ${label}…`)}
          onChange={(e) => onChange(name, e.target.value)}
          onBlur={onBlur ? () => onBlur(name, value) : undefined} />
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: 3 }}>
        <div>
          {hint && !err && <div style={{ fontSize: 10, color: "#94A3B8" }}>{hint}</div>}
          {err && <div style={ERR_TXT}>{err}</div>}
        </div>
        {exactLen && !readOnly && (
          <div style={{ fontSize: 10, color: currentLen === exactLen ? "#16A34A" : "#94A3B8", flexShrink: 0, marginLeft: 8 }}>
            {currentLen}/{exactLen}
          </div>
        )}
        {rangeLen && !readOnly && (
          <div style={{ fontSize: 10, color: currentLen >= rangeLen.min && currentLen <= rangeLen.max ? "#16A34A" : "#94A3B8", flexShrink: 0, marginLeft: 8 }}>
            {currentLen}/{rangeLen.max}
          </div>
        )}
      </div>
    </div>
  );
}
Field.propTypes = {
  label: PropTypes.string.isRequired, name: PropTypes.string.isRequired,
  value: PropTypes.any, onChange: PropTypes.func.isRequired, onBlur: PropTypes.func,
  readOnly: PropTypes.bool, type: PropTypes.string, required: PropTypes.bool,
  options: PropTypes.array, placeholder: PropTypes.string, hint: PropTypes.string,
  errors: PropTypes.object,
};

// ─── Main component ───────────────────────────────────────────────────────────
// accessMatrix: { group_code → 'editable' | 'view_only' | 'no_access' }
// When not provided defaults to all-editable (initiator/create context).
const VendorCreationForm = ({ formData, onChange, readOnly, errors: externalErrors, accessMatrix, requestTypeCode }) => {
  // BUG-55 — SAP vendor code visibility per flow:
  //   vendor_creation / vendor_creation_seasonal → hide input (SAP issues it on push)
  //   vendor_change / vendor_extend              → read-only display from form_data.sap_vendor_number
  //   anything else (defensive)                  → keep legacy free-text input
  const isNewVendorFlow      = requestTypeCode === "vendor_creation" || requestTypeCode === "vendor_creation_seasonal";
  const isExistingVendorFlow = requestTypeCode === "vendor_change"   || requestTypeCode === "vendor_extend";
  const [tab,      setTab]      = useState("initial");

  // Resolve effective access for a group: explicit matrix > global readOnly prop > editable
  const getAccess = (groupCode) => {
    if (accessMatrix && accessMatrix[groupCode]) return accessMatrix[groupCode];
    if (readOnly) return "view_only";
    return "editable";
  };
  const [errors,   setErrors]   = useState({});

  // Lookups
  const [companyCodes,   setCompanyCodes]   = useState([]);
  const [plantCodes,     setPlantCodes]     = useState([]);
  const [paymentTerms,   setPaymentTerms]   = useState([]);
  const [deliveryTerms,  setDeliveryTerms]  = useState([]);
  const [accountGroups,  setAccountGroups]  = useState([]);
  const [currencies,     setCurrencies]     = useState([]);
  // BUG-49 — countries loaded from /api/t2t/lookups/country
  const [countryOptions, setCountryOptions] = useState(FALLBACK_COUNTRY_OPTIONS);
  // BUG-50 — full country metadata (iso2 → {phone_code, phone_min_length, phone_max_length})
  const [countryMetaByIso2, setCountryMetaByIso2] = useState({});
  // BUG-53 — banks list (datalist source) + IFSC verification state
  const [bankList,     setBankList]     = useState([]);
  const [ifscResult,   setIfscResult]   = useState(null);  // { ifsc, bank_name, bank_code, branch, address, city, state }
  const [ifscError,    setIfscError]    = useState(null);
  const [ifscLoading,  setIfscLoading]  = useState(false);
  // Default India phone metadata so the +91 badge + 10-digit rule render
  // immediately on first paint, even before the API resolves.
  const phoneMetaForIso2 = (iso2) => countryMetaByIso2[iso2]
    || (iso2 === "IN" ? { phone_code: "91", phone_min_length: 10, phone_max_length: 10 } : null);

  useEffect(() => {
    getLookupByType("company_code").then((r)  => { if (!r.error) setCompanyCodes((r.data?.data || []).map((x) => ({ value: x.code, label: `${x.code} — ${x.label}` }))); });
    getLookupByType("plant_code").then((r)    => { if (!r.error) setPlantCodes((r.data?.data || []).map((x) => ({ value: x.code, label: `${x.code} — ${x.label}` }))); });
    getLookupByType("payment_term").then((r)  => { if (!r.error) setPaymentTerms((r.data?.data || []).map((x) => ({ value: x.code, label: `${x.code} — ${x.label}` }))); });
    getLookupByType("delivery_term").then((r) => { if (!r.error) setDeliveryTerms((r.data?.data || []).map((x) => ({ value: x.code, label: `${x.code} — ${x.label}` }))); });
    getLookupByType("account_group").then((r) => { if (!r.error) setAccountGroups((r.data?.data || []).map((x) => ({ value: x.code, label: `${x.code} — ${x.label}` }))); });
    getLookupByType("currency").then((r)      => { if (!r.error) setCurrencies((r.data?.data || []).map((x) => ({ value: x.code, label: `${x.code} — ${x.label}` }))); });
    // BUG-49 + BUG-50 — country lookup includes phone metadata (phone_code, phone_min_length, phone_max_length)
    getData("t2t/lookups/country").then((r)   => {
      if (r.error) return;
      const list = r.data?.data;
      if (Array.isArray(list) && list.length > 0) {
        setCountryOptions(list.map((c) => ({ value: c.iso2, label: c.name })));
        const meta = {};
        list.forEach((c) => {
          meta[c.iso2] = {
            phone_code:        c.phone_code,
            phone_min_length:  c.phone_min_length,
            phone_max_length:  c.phone_max_length,
          };
        });
        setCountryMetaByIso2(meta);
      }
    });
    // BUG-53 — bank list for the Finance tab Bank Name datalist
    getData("t2t/lookups/bank").then((r) => {
      if (r.error) return;
      const list = r.data?.data;
      if (Array.isArray(list) && list.length > 0) setBankList(list);
    });
  }, []);

  // BUG-53 — IFSC live verification. Runs on every IFSC value change that
  // passes the shape regex. Always-overwrite of Bank Name. Mismatch banner
  // surfaces when typed bank_name (free text portion that survives in
  // formData.bank_name) doesn't match the API-returned bank_name.
  useEffect(() => {
    const ifsc = String(formData.ifsc_code || "").toUpperCase().trim();
    if (!ifscRegex.test(ifsc)) {
      setIfscResult(null);
      setIfscError(null);
      return;
    }
    let cancelled = false;
    setIfscLoading(true);
    setIfscError(null);
    getData(`t2t/lookups/ifsc/${encodeURIComponent(ifsc)}`)
      .then((r) => {
        if (cancelled) return;
        if (r.error) {
          setIfscResult(null);
          setIfscError(r.error.status === 404
            ? `IFSC "${ifsc}" not found in the IFSC directory.`
            : `IFSC verification failed: ${r.error.message || 'unknown'}`);
          return;
        }
        const data = r.data?.data;
        if (!data) { setIfscResult(null); return; }
        setIfscResult(data);
        // Always-overwrite Bank Name with the authoritative API result (pincode pattern)
        if (data.bank_name && data.bank_name !== formData.bank_name) {
          onChange("bank_name", data.bank_name);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setIfscError("IFSC verification temporarily unavailable. Please try again.");
        setIfscResult(null);
      })
      .finally(() => { if (!cancelled) setIfscLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.ifsc_code]);

  // PAN TDS auto-logic
  useEffect(() => {
    if (readOnly) return;
    const pan = formData.pan || "";
    if (pan && panRegex.test(pan.toUpperCase())) {
      onChange("tds_code", "FC2");
      onChange("wht_code", "FC2");
    } else {
      onChange("tds_code", "FC1");
      onChange("wht_code", "FC1");
    }
    onChange("section_code", "194C");
  }, [formData.pan]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (key, value) => {
    // BUG-58: auto-uppercase SAP / govt-ID fields so stored value matches backend regex
    const normalised = (UPPERCASE_FIELDS.has(key) && typeof value === "string") ? value.toUpperCase() : value;
    onChange(key, normalised);
    const err = validate(key, normalised);
    setErrors((prev) => ({ ...prev, [key]: err }));
  };

  const handleBlur = (key, value) => {
    const normalised = (UPPERCASE_FIELDS.has(key) && typeof value === "string") ? value.toUpperCase() : value;
    const err = validate(key, normalised);
    setErrors((prev) => ({ ...prev, [key]: err || undefined }));

    // BUG-46 — pincode auto-fill: on blur of a 6-digit pincode, fetch
    // state/district/area from the backend cache (fallback India Post API)
    // and overwrite State / City. Pincode is treated as the source of truth
    // for these fields — the dropdown enforcement on State means there's no
    // valid user-edited state that an authoritative pincode lookup should
    // preserve. (Follow-up: always-overwrite per user direction.)
    if (key === "pin_code" && /^\d{6}$/.test(value || "")) {
      const country = (formData.country_code || "IN").toString();
      const isIndia = country === "India" || country === "IN" || /india/i.test(country);
      if (!isIndia) return;
      getData(`t2t/lookups/pincode/${encodeURIComponent(value)}`).then((res) => {
        if (res.error || !res.data?.data) return;
        const row = res.data.data;
        // State must match a dropdown option — normalise via matchIndianState.
        // If India Post returns something we can't map (unusual), fall back to raw value.
        const matched = matchIndianState(row.state) || row.state;
        if (matched)      onChange("state", matched);
        if (row.district) onChange("city",  row.district);
      }).catch(() => { /* silent — autofill is a courtesy */ });
    }
  };

  // BUG-58 — live PAN ↔ GSTIN cross-check. Per GST spec, chars 3-12 of a GSTIN
  // must equal the entity's PAN. We only run the comparison when both values
  // already pass format validation, so we don't fight with the format-error path.
  // Mismatch is surfaced as an error on the GSTIN field (typed-second is friendlier).
  useEffect(() => {
    const pan   = (formData.pan   || "").trim();
    const gstin = (formData.gstin || "").trim();

    setErrors((prev) => {
      // Clear any previously-set mismatch error when conditions no longer apply.
      const clearMismatch = (next) => {
        if (next.gstin === GSTIN_PAN_MISMATCH_MSG) {
          const { gstin: _drop, ...rest } = next;
          return rest;
        }
        return next;
      };

      if (!pan || !gstin || !panRegex.test(pan) || !gstinRegex.test(gstin)) {
        return clearMismatch(prev);
      }
      const match = gstin.slice(2, 12) === pan;
      if (match) return clearMismatch(prev);
      return { ...prev, gstin: GSTIN_PAN_MISMATCH_MSG };
    });
  }, [formData.pan, formData.gstin]);

  // BUG-46 — live pincode/state mismatch warning. Fires when both fields are
  // filled. Backend validate endpoint returns { match, expected_state, ... }.
  // We surface it as a warning under the State field. Hard block happens
  // server-side at submit time (separate path).
  const [pinStateWarning, setPinStateWarning] = useState(null);
  useEffect(() => {
    const pin   = (formData.pin_code || "").trim();
    const state = (formData.state    || "").trim();
    const country = (formData.country_code || "India").toString();
    const isIndia = country === "India" || country === "IN" || /india/i.test(country);
    if (!isIndia || !/^\d{6}$/.test(pin) || !state) {
      setPinStateWarning(null);
      return;
    }
    let cancelled = false;
    getData(`t2t/lookups/pincode/${encodeURIComponent(pin)}/validate?state=${encodeURIComponent(state)}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        if (!data) { setPinStateWarning(null); return; }
        setPinStateWarning(data.match ? null : data);
      })
      .catch(() => { if (!cancelled) setPinStateWarning(null); });
    return () => { cancelled = true; };
  }, [formData.pin_code, formData.state, formData.country_code]);

  const allErrors = { ...errors, ...(externalErrors || {}) };
  const isDomestic = (formData.vendor_type || "domestic") !== "international";
  const hasAccountGroup = !!(formData.account_group && String(formData.account_group).trim());

  const f = (name) => formData[name] || "";

  // Per-tab shared props: readOnly when access is not 'editable'
  const tabFp = (groupCode) => {
    const access = getAccess(groupCode);
    return { onChange: handleChange, onBlur: handleBlur, readOnly: access !== "editable", errors: allErrors };
  };

  // Visible tabs: hide those with no_access
  const visibleTabs = TABS.filter((t) => getAccess(t.id) !== "no_access");

  // All fields per tab (for scoped format validation)
  const TAB_FIELDS = {
    initial:   ["account_group", "company_code", "vendor_name", "vendor_name_2", "vendor_type", "vendor_category"],
    general:   ["street", "city", "state", "pin_code", "country_code", "contact_name", "contact_phone", "contact_email"],
    tax:       ["pan", "gstin", "msme_reg", "cin", "vat_number", "tax_id"],
    finance:   ["bank_name", "bank_account_number", "ifsc_code", "swift_code", "iban", "bank_account_type", "payment_terms", "reconciliation_account", "currency"],
    purchasing:["plant_code", "purchasing_org", "order_currency", "delivery_terms", "incoterms", "incoterms_2"],
    partner:   ["partner_role", "partner_number", "vendor_notes"],
  };

  // Required fields per tab — VAL3: if account_group filled, purchasing_org + order_currency become mandatory
  const TAB_REQUIRED = {
    initial:    ["account_group", "company_code", "vendor_name", "vendor_type"],
    general:    ["street", "city", "contact_name", "contact_email"],
    tax:        isDomestic ? ["pan"] : [],
    finance:    isDomestic ? ["bank_name", "bank_account_number", "ifsc_code"] : ["bank_name", "bank_account_number"],
    purchasing: hasAccountGroup ? ["purchasing_org", "order_currency"] : [],
    partner:    [],
  };

  // Validate current tab — returns true if clean, false if errors found
  const validateTab = (tabId) => {
    if (getAccess(tabId) !== "editable") return true; // view-only tabs skip validation
    const newErrors = { ...errors };
    let clean = true;

    // Required field check
    for (const key of (TAB_REQUIRED[tabId] || [])) {
      const val = formData[key];
      if (!val || String(val).trim() === "") {
        newErrors[key] = "This field is required";
        clean = false;
      } else {
        // Clear stale required error if now filled
        if (newErrors[key] === "This field is required") delete newErrors[key];
      }
    }

    // Format validation — only fields that belong to this tab
    for (const key of (TAB_FIELDS[tabId] || [])) {
      const formatErr = validate(key, formData[key]);
      if (formatErr) { newErrors[key] = formatErr; clean = false; }
      else if (newErrors[key] && newErrors[key] !== "This field is required") delete newErrors[key];
    }

    setErrors(newErrors);
    return clean;
  };

  return (
    <div>
      {/* Tab bar */}
      <div style={{ display: "flex", gap: 2, background: "#F1F5F9", padding: 4, borderRadius: 8, marginBottom: 24, flexWrap: "wrap" }}>
        {visibleTabs.map((t) => {
          const access = getAccess(t.id);
          return (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
              background: tab === t.id ? "#fff" : "transparent",
              color: tab === t.id ? "#0F172A" : "#64748B",
              boxShadow: tab === t.id ? "0 1px 3px rgba(0,0,0,.08)" : "none",
              display: "flex", alignItems: "center", gap: 4 }}>
            {access === "view_only" && <i className="bi bi-lock-fill" style={{ fontSize: 9, color: "#94A3B8" }} title="View only"></i>}
            {t.label}
          </button>
          );
        })}
      </div>

      {/* ── Tab 1: Initial ── */}
      {tab === "initial" && (
        <div>
          {!hasAccountGroup && (
            <div style={{ fontSize: 11, color: "#D97706", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 6, padding: "6px 10px", marginBottom: 12 }}>
              Selecting an Account Group will mark additional fields (Purchasing Org, Order Currency) as required.
            </div>
          )}
          <div style={ROW}>
            <Field label="Account Group" name="account_group" value={f("account_group")} options={accountGroups} required {...tabFp("initial")} />
            <Field label="Company Code"  name="company_code"  value={f("company_code")}  options={companyCodes}  required {...tabFp("initial")} />
          </div>
          <div style={ROW}>
            <Field label="Vendor Name"   name="vendor_name"   value={f("vendor_name")}   required placeholder="Full legal name" {...tabFp("initial")} />
            <Field label="Vendor Name 2" name="vendor_name_2" value={f("vendor_name_2")} placeholder="Additional name line (optional)" {...tabFp("initial")} />
          </div>
          <div style={ROW}>
            <Field label="Vendor Type" name="vendor_type" value={f("vendor_type")} required
              options={[{ value: "domestic", label: "Domestic" }, { value: "international", label: "International" }]} {...tabFp("initial")} />
            <Field label="Vendor Category" name="vendor_category" value={f("vendor_category")}
              options={[
                { value: "goods",    label: "Goods" },
                { value: "services", label: "Services" },
                { value: "both",     label: "Goods & Services" },
              ]} {...tabFp("initial")} />
          </div>
        </div>
      )}

      {/* ── Tab 2: General Data ── */}
      {tab === "general" && (
        <div>
          <Field label="Street / Address Line 1" name="street"  value={f("street")}  required type="textarea" {...tabFp("general")} />
          <div style={ROW}>
            <Field label="City"      name="city"       value={f("city")}      required {...tabFp("general")} />
            {/* BUG-46 follow-up: State is now a dropdown of 28 Indian states + 8 UTs.
                Auto-fill from pincode lookup normalises India Post's state name
                to the dropdown's canonical option. */}
            <Field label="State"     name="state"      value={f("state")}     options={INDIAN_STATES.map((s) => ({ value: s, label: s }))} {...tabFp("general")} />
          </div>
          <div style={ROW}>
            <Field label="Pin Code"     name="pin_code"      value={f("pin_code")}     hint="Exactly 6 digits" {...tabFp("general")} />
            {/* BUG-46 follow-up: Country Code dropdown. Stores ISO-2 (SAP master-data convention),
                displays full country name. India default at top. */}
            <Field label="Country Code" name="country_code"  value={f("country_code") || "IN"} options={countryOptions} {...tabFp("general")} />
          </div>
          {/* BUG-46 — live pincode/state mismatch warning */}
          {pinStateWarning && (
            <div style={{
              padding: "8px 12px",
              background: "#FFFBEB",
              border: "1px solid #FDE68A",
              borderRadius: 6,
              fontSize: 12,
              color: "#92400E",
              marginTop: -4,
              marginBottom: 12,
            }}>
              <strong>⚠ Pincode / State mismatch:</strong> Pincode <code style={{ background: "#FEF3C7", padding: "1px 4px", borderRadius: 3 }}>{pinStateWarning.pincode}</code> belongs to <strong>{pinStateWarning.expected_state}</strong>, but the State you entered is <strong>{pinStateWarning.supplied_state}</strong>. Please correct one before submitting — the form will be blocked otherwise.
            </div>
          )}
          <div style={ROW}>
            <Field label="Contact Person"  name="contact_name"  value={f("contact_name")}  required {...tabFp("general")} />
            {/* BUG-50 — Contact Phone with country-aware "+code" prefix + per-country length validation */}
            {(() => {
              const ctry = countryOptions.find((c) => c.value === (formData.country_code || "IN"));
              // countryOptions is {value: iso2, label: name}; phone metadata isn't there yet.
              // Look up via the full country lookup on demand via the global allCountries state if available;
              // for now, fall back to a small inline map of common codes. The full metadata lives in
              // the country_lookup row — we fetch it lazily in the effect below.
              const phoneCode = phoneMetaForIso2(formData.country_code || "IN")?.phone_code || "";
              const minLen = phoneMetaForIso2(formData.country_code || "IN")?.phone_min_length || 10;
              const maxLen = phoneMetaForIso2(formData.country_code || "IN")?.phone_max_length || 15;
              const digits = (f("contact_phone") || "").replace(/\D/g, "");
              const inRange = digits.length === 0 || (digits.length >= minLen && digits.length <= maxLen);
              const hint = minLen === maxLen ? `Exactly ${minLen} digits` : `${minLen}–${maxLen} digits`;
              const access = getAccess("general");
              const readOnly = access !== "editable";
              const phoneErr = !inRange && digits.length > 0
                ? `Must be ${minLen === maxLen ? `exactly ${minLen}` : `${minLen}–${maxLen}`} digits for the selected country.`
                : null;
              return (
                <div style={FG}>
                  <label style={LBL}>Contact Phone</label>
                  <div style={{ display: "flex", alignItems: "stretch" }}>
                    {phoneCode && (
                      <span style={{
                        display: "inline-flex", alignItems: "center",
                        padding: "0 10px", fontSize: 13, color: "#334155",
                        background: "#F1F5F9", border: "1px solid #E2E8F0",
                        borderRight: "none",
                        borderTopLeftRadius: 6, borderBottomLeftRadius: 6,
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        whiteSpace: "nowrap",
                      }}>+{phoneCode}</span>
                    )}
                    <input
                      type="tel"
                      readOnly={readOnly}
                      style={{
                        ...(phoneErr ? FC_ERR : { ...FC, background: readOnly ? "#F8FAFC" : "#fff" }),
                        ...(phoneCode ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } : {}),
                        flex: 1,
                      }}
                      value={f("contact_phone")}
                      placeholder={readOnly ? "" : (minLen === maxLen ? `${"X".repeat(minLen)} (${minLen} digits)` : `${minLen}–${maxLen} digits`)}
                      maxLength={maxLen + 4}
                      onChange={(e) => handleChange("contact_phone", e.target.value.replace(/[^\d\s-]/g, ""))}
                      onBlur={() => handleBlur("contact_phone", f("contact_phone"))}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: 3 }}>
                    <div>
                      {phoneErr && <div style={ERR_TXT}>{phoneErr}</div>}
                      {!phoneErr && <div style={{ fontSize: 10, color: "#94A3B8" }}>{hint}</div>}
                    </div>
                    {!readOnly && (
                      <div style={{ fontSize: 10, color: inRange ? "#16A34A" : "#DC2626", flexShrink: 0, marginLeft: 8, fontWeight: 600 }}>
                        {digits.length}/{maxLen}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
          <Field label="Contact Email" name="contact_email" value={f("contact_email")} type="email" required {...tabFp("general")} />
        </div>
      )}

      {/* ── Tab 3: Tax ── */}
      {tab === "tax" && (
        <div>
          {isDomestic ? (
            <>
              <div style={ROW}>
                <Field label="PAN Number" name="pan" value={f("pan")} required
                  placeholder="ABCDE1234F" hint="5 letters + 4 digits + 1 letter (uppercase)" {...tabFp("tax")} />
                <Field label="GSTIN" name="gstin" value={f("gstin")}
                  placeholder="22AAAAA0000A1Z5" hint="15-character GST identification number" {...tabFp("tax")} />
              </div>
              <div style={ROW}>
                <Field label="MSME Registration No." name="msme_reg" value={f("msme_reg")} {...tabFp("tax")} />
                <Field label="CIN (if company)"      name="cin"      value={f("cin")} {...tabFp("tax")} />
              </div>
              <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, padding: "12px 16px", marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#15803D", marginBottom: 8 }}>TDS Settings (auto-set)</div>
                <div style={ROW}>
                  <Field label="Withholding Tax Code" name="tds_code" value={f("tds_code") || (f("pan") ? "FC2" : "FC1")} readOnly={true} onChange={onChange} errors={allErrors} />
                  <Field label="Section Code" name="section_code" value="194C" readOnly={true} onChange={onChange} errors={allErrors} hint="Locked to 194C for all Indian vendors" />
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={ROW}>
                <Field label="VAT Number" name="vat_number" value={f("vat_number")} {...tabFp("tax")} />
                <Field label="Tax ID"     name="tax_id"     value={f("tax_id")}     {...tabFp("tax")} />
              </div>
              <Field label="Country of Origin" name="country_code" value={f("country_code")} {...tabFp("tax")} />
            </>
          )}
        </div>
      )}

      {/* ── Tab 4: Finance ── */}
      {tab === "finance" && (
        <div>
          {/* BUG-53 — Bank Name uses a <datalist> sourced from t2t_bank_lookup.
              Free-text fallback retained so admins can enter a niche cooperative
              not in the seed list. IFSC verification (below) always-overwrites
              with the authoritative API value. */}
          <div style={ROW}>
            <div style={FG}>
              <label style={LBL}>Bank Name<span style={REQ_STAR}>*</span></label>
              <input
                style={(allErrors?.bank_name) ? FC_ERR : { ...FC, background: (getAccess("finance") !== "editable") ? "#F8FAFC" : "#fff" }}
                type="text"
                list="t2t-bank-list"
                readOnly={getAccess("finance") !== "editable"}
                placeholder={getAccess("finance") !== "editable" ? "" : "Type to search…"}
                value={f("bank_name")}
                onChange={(e) => handleChange("bank_name", e.target.value)}
                onBlur={() => handleBlur("bank_name", f("bank_name"))}
              />
              <datalist id="t2t-bank-list">
                {bankList.map((b) => (
                  <option key={b.bank_code} value={b.bank_name} />
                ))}
              </datalist>
              {allErrors?.bank_name && <div style={ERR_TXT}>{allErrors.bank_name}</div>}
            </div>
            <Field label="Account Number"   name="bank_account_number" value={f("bank_account_number")} required {...tabFp("finance")} />
          </div>
          {isDomestic ? (
            <>
              <div style={ROW}>
                <Field label="IFSC Code" name="ifsc_code" value={f("ifsc_code")} required placeholder="HDFC0001234" {...tabFp("finance")} />
                <Field label="Account Type" name="bank_account_type" value={f("bank_account_type")}
                  options={[{ value: "current", label: "Current" }, { value: "savings", label: "Savings" }]} {...tabFp("finance")} />
              </div>
              {/* BUG-53 — IFSC verification result block */}
              {ifscLoading && (
                <div style={{ padding: "8px 12px", fontSize: 11, color: "#64748B", marginBottom: 12 }}>
                  Verifying IFSC with the IFSC directory…
                </div>
              )}
              {!ifscLoading && ifscError && (
                <div style={{ padding: "8px 12px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, fontSize: 12, color: "#991B1B", marginBottom: 12 }}>
                  ⚠ {ifscError}
                </div>
              )}
              {!ifscLoading && ifscResult && (
                <div style={{ padding: "10px 12px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, fontSize: 12, color: "#166534", marginBottom: 12, lineHeight: 1.6 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    ✓ IFSC verified — {ifscResult.bank_name}
                  </div>
                  <div><strong>Branch:</strong> {ifscResult.branch || "—"}</div>
                  <div><strong>Address:</strong> {ifscResult.address || "—"}</div>
                  <div><strong>City / State:</strong> {[ifscResult.city, ifscResult.state].filter(Boolean).join(", ") || "—"}</div>
                  {ifscResult.contact && <div><strong>Branch contact:</strong> {ifscResult.contact}</div>}
                </div>
              )}
            </>
          ) : (
            <div style={ROW}>
              <Field label="SWIFT Code" name="swift_code" value={f("swift_code")} {...tabFp("finance")} />
              <Field label="IBAN"       name="iban"       value={f("iban")}       {...tabFp("finance")} />
            </div>
          )}
          <div style={ROW}>
            <Field label="Payment Terms"        name="payment_terms"        value={f("payment_terms")}        options={paymentTerms}  {...tabFp("finance")} />
            <Field label="Reconciliation A/C"   name="reconciliation_account" value={f("reconciliation_account")} {...tabFp("finance")} />
          </div>
          <Field label="Currency" name="currency" value={f("currency") || "INR"} options={currencies} {...tabFp("finance")} />
        </div>
      )}

      {/* ── Tab 5: Purchasing ── */}
      {tab === "purchasing" && (
        <div>
          <div style={ROW}>
            <Field label="Plant Code"           name="plant_code"           value={f("plant_code")}           options={plantCodes}    {...tabFp("purchasing")} />
            <Field label="Purchasing Organisation" name="purchasing_org"    value={f("purchasing_org")}        required={hasAccountGroup} {...tabFp("purchasing")} />
          </div>
          <div style={ROW}>
            <Field label="Order Currency"  name="order_currency"  value={f("order_currency")}  options={currencies}    required={hasAccountGroup} {...tabFp("purchasing")} />
            <Field label="Delivery Terms"  name="delivery_terms"  value={f("delivery_terms")}  options={deliveryTerms} {...tabFp("purchasing")} />
          </div>
          <div style={ROW}>
            <Field label="Incoterms"       name="incoterms"       value={f("incoterms")}       placeholder="e.g. CIF Mumbai" {...tabFp("purchasing")} />
            <Field label="Incoterms 2"     name="incoterms_2"     value={f("incoterms_2")}     {...tabFp("purchasing")} />
          </div>
        </div>
      )}

      {/* ── Tab 6: Partner Function ── */}
      {tab === "partner" && (
        <div>
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 16 }}>
            Define the roles this vendor will play in SAP transactions.
          </div>
          <div style={ROW}>
            <Field label="Partner Role" name="partner_role" value={f("partner_role")}
              options={[
                { value: "LF", label: "LF — Vendor (Main)" },
                { value: "FS", label: "FS — Invoicing Party" },
                { value: "AZ", label: "AZ — Payee" },
                { value: "SP", label: "SP — Goods Supplier" },
              ]} {...tabFp("partner")} />
            {/* BUG-55 — Partner Number (SAP) is issued by SAP, not entered up-front */}
            {isNewVendorFlow ? (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", marginBottom: 6 }}>Partner Number (SAP)</div>
                <div style={{
                  padding: "8px 12px", border: "1px dashed #CBD5E1", borderRadius: 6,
                  background: "#F8FAFC", color: "#64748B", fontSize: 12, fontStyle: "italic",
                }}>
                  Will be assigned by SAP after approval.
                </div>
              </div>
            ) : isExistingVendorFlow ? (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", marginBottom: 6 }}>Partner Number (SAP)</div>
                <div style={{
                  padding: "8px 12px", border: "1px solid #E2E8F0", borderRadius: 6,
                  background: "#F1F5F9", color: "#0F172A", fontSize: 13, fontFamily: "monospace",
                }}>
                  {f("sap_vendor_number") || f("partner_number") || <span style={{ color: "#94A3B8", fontStyle: "italic", fontFamily: "inherit" }}>(not on file)</span>}
                </div>
              </div>
            ) : (
              <Field label="Partner Number (SAP)" name="partner_number" value={f("partner_number")} placeholder="SAP vendor no. if known" {...tabFp("partner")} />
            )}
          </div>
          <Field label="Additional Notes" name="vendor_notes" value={f("vendor_notes")} type="textarea" {...tabFp("partner")} />
        </div>
      )}

      {/* Tab navigation footer — uses visibleTabs so Prev/Next skip hidden tabs */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24, paddingTop: 16, borderTop: "1px solid #E2E8F0" }}>
        <button type="button"
          onClick={() => { const i = visibleTabs.findIndex((t) => t.id === tab); if (i > 0) setTab(visibleTabs[i - 1].id); }}
          disabled={tab === visibleTabs[0]?.id}
          style={{ padding: "7px 16px", border: "1px solid #E2E8F0", borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, opacity: tab === visibleTabs[0]?.id ? 0.4 : 1 }}>
          ← Previous
        </button>
        <span style={{ fontSize: 11, color: "#94A3B8" }}>
          Tab {visibleTabs.findIndex((t) => t.id === tab) + 1} of {visibleTabs.length}
        </span>
        <button type="button"
          onClick={() => {
            if (!validateTab(tab)) return;
            const i = visibleTabs.findIndex((t) => t.id === tab);
            if (i < visibleTabs.length - 1) setTab(visibleTabs[i + 1].id);
          }}
          disabled={tab === visibleTabs[visibleTabs.length - 1]?.id}
          style={{ padding: "7px 16px", border: "none", borderRadius: 6, background: "var(--t2t-primary, #36B82A)", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, opacity: tab === visibleTabs[visibleTabs.length - 1]?.id ? 0.4 : 1 }}>
          Next →
        </button>
      </div>
    </div>
  );
};

VendorCreationForm.propTypes = {
  formData:        PropTypes.object.isRequired,
  onChange:        PropTypes.func.isRequired,
  readOnly:        PropTypes.bool,
  errors:          PropTypes.object,
  accessMatrix:    PropTypes.object,
  requestTypeCode: PropTypes.string, // BUG-55 — drives Partner Number (SAP) visibility
};

VendorCreationForm.defaultProps = {
  readOnly:        false,
  errors:          {},
  accessMatrix:    null,
  requestTypeCode: null,
};

export default VendorCreationForm;
